<template>
  <div
    class="fullscreen-page"
    @click="toggleCleanMode"
    :class="{ 'clean-mode': isCleanMode, 'settings-open': showSettings }"
  >
    <!-- 背景装饰 -->
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <desktop-brand-corner />

    <button type="button" class="back-btn" @click.stop="handleGoBack" aria-label="返回上一页">
      <font-awesome-icon icon="chevron-left" />
    </button>

    <!-- 顶部：中文意思 -->
    <header class="page-header" :class="{ hidden: !showTranslation }">
      <h1 class="word-translation" :class="{ visible: true }">
        {{ currentCard.zh }}
      </h1>
    </header>

    <!-- 右侧功能按钮 -->
    <div class="side-controls" :class="{ hidden: isCleanMode }">
      <button
        class="control-btn favorite-btn"
        :class="{ active: isCurrentCardFavorite }"
        :disabled="isCurrentFavoriteLoading"
        @click.stop="toggleFavorite"
        :title="isCurrentCardFavorite ? '取消收藏' : '收藏'"
      >
        <font-awesome-icon icon="heart" />
      </button>
      <button class="control-btn" @click.stop="openSettings" title="设置">
        <font-awesome-icon icon="cog" />
      </button>
      <button
        class="control-btn auto-play-btn"
        :class="{ active: isAutoMode }"
        @click.stop="startAutoPlay"
        :title="isAutoMode ? '自动播放中' : '开始自动播放'"
      >
        <font-awesome-icon :icon="isAutoMode ? 'book-open' : ['fas', 'hand-pointer']" />
      </button>
    </div>

    <!-- 中间内容区域 -->
    <div class="main-content">
      <!-- 视频展示 -->
      <div
        class="video-container"
        :style="{ '--tpr-video-aspect-ratio': currentVideoAspectRatio }"
        @click.stop="togglePlay"
      >
        <div class="video-wrapper">
          <video
            :key="currentCard.id"
            ref="fullscreenVideoRef"
            class="fullscreen-video"
            :class="{ 'is-ready': isVideoReady }"
            :src="currentCard.videoUrl"
            :poster="currentCard.coverUrl"
            playsinline
            preload="auto"
            controlslist="nodownload nofullscreen noremoteplayback"
            disablepictureinpicture
            @loadedmetadata="handleVideoMetadata"
            @loadeddata="handleVideoLoadedData"
            @play="handleVideoPlay"
            @pause="handleVideoPause"
            @ended="handleVideoEnded"
          ></video>

          <img
            v-if="!isVideoReady && currentCard.coverUrl"
            class="fullscreen-video-cover"
            :src="currentCard.coverUrl"
            alt=""
          />

          <div v-if="!isPlaying" class="video-status" aria-hidden="true">
            <font-awesome-icon :icon="cards.length === 0 ? 'hand-paper' : 'play'" />
          </div>
        </div>
      </div>

      <!-- 单词展示 -->
      <h2 class="word-display" :class="{ visible: true }">
        {{ currentCard.en }}
      </h2>
    </div>

    <!-- 底部控制栏 -->
    <footer class="bottom-controls" :class="{ hidden: isCleanMode }">
      <div class="play-section">
        <button class="play-btn" @click.stop="togglePlay">
          <font-awesome-icon :icon="isPlaying ? 'pause' : 'play'" />
        </button>
      </div>
    </footer>

    <!-- 左下角进度 -->
    <div class="progress-indicator">
      <span class="progress-text">{{ cards.length === 0 ? 0 : currentIndex + 1 }}/{{ cards.length }}</span>
    </div>

    <!-- 左右切换按钮 -->
    <button class="nav-btn prev" @click.stop="prevCard">
      <font-awesome-icon icon="chevron-left" />
    </button>
    <button class="nav-btn next" @click.stop="nextCard">
      <font-awesome-icon icon="chevron-right" />
    </button>

    <!-- 触摸滑动区域 -->
    <div class="swipe-area" ref="swipeArea">
    </div>

    <!-- 设置弹窗 -->
    <div class="settings-modal" v-if="showSettings" @click.stop="closeSettings">
      <div class="settings-content" @click.stop>
        <div class="settings-list">
          <div class="settings-item">
            <div class="settings-text">
              <p class="settings-title">显示翻译</p>
              <p class="settings-desc">翻译显示在绘本顶部</p>
            </div>
            <label class="switch">
              <input type="checkbox" v-model="showTranslation">
              <span class="slider"></span>
            </label>
          </div>
          <div class="settings-item">
            <div class="settings-text">
              <p class="settings-title">手动翻页时音频自动播放</p>
            </div>
            <label class="switch">
              <input type="checkbox" v-model="manualFlipAutoPlay">
              <span class="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getTprList, type TprVO } from '@/api/course-tpr'
import { addHistory } from '@/api/history'
import { getFavorites, addFavorites, deleteFavorites } from '@/api/favorites'
import { getAccessToken } from '@/config/axios/auth'

const isGuestUser = computed(() => getAccessToken() == null)
const isNonVipUser = computed(() =>
  !isGuestUser.value && localStorage.getItem('vipTag') === 'false'
)

const route = useRoute()
const router = useRouter()
const DEFAULT_TPR_CATEGORY = '身体指令'
const TPR_CONTENT_TYPE = 3

const tprList = ref<TprVO[]>([])
const isLoading = ref(false)
const historyRecordedIds = new Set<number>()

const currentIndex = ref(0)
const isCleanMode = ref(false)
const isPlaying = ref(false)
const isVideoReady = ref(false)
const isAutoMode = ref(false)
const autoPlayTimer = ref(null)
const showSettings = ref(false)
const showTranslation = ref(true)
const manualFlipAutoPlay = ref(true)
const favoriteCardIds = ref([])
const favoriteLoadingIds = ref([])
const fullscreenVideoRef = ref(null)
const videoAspectRatios = ref<Record<string, string>>({})

const queryParams = ref({
  category: route.query.category || DEFAULT_TPR_CATEGORY,
  stage: route.query.stage
})

const cardColors = [
  'rgba(255, 140, 66, 0.15)',
  'rgba(91, 143, 249, 0.15)',
  'rgba(76, 175, 80, 0.15)',
  'rgba(231, 76, 60, 0.15)',
  'rgba(255, 193, 7, 0.15)',
  'rgba(156, 39, 176, 0.15)',
  'rgba(0, 150, 136, 0.15)',
  'rgba(33, 150, 243, 0.15)'
]

const cards = computed(() => {
  return tprList.value.map((item, index) => ({
    id: item.id,
    en: item.english,
    zh: item.chinese,
    videoUrl: item.videoUrl,
    coverUrl: item.coverUrl,
    bgColor: cardColors[index % cardColors.length]
  }))
})

const emptyCard = {
  id: 0,
  en: '',
  zh: '',
  videoUrl: '',
  coverUrl: '',
  bgColor: 'rgba(255, 255, 255, 0.5)'
}

// 当前卡片
const currentCard = computed(() => cards.value[currentIndex.value] || emptyCard)
const currentVideoAspectRatio = computed(() => {
  return videoAspectRatios.value[String(currentCard.value.id)] || '4 / 5'
})
const isCurrentCardFavorite = computed(() => favoriteCardIds.value.includes(currentCard.value.id))
const isCurrentFavoriteLoading = computed(() => favoriteLoadingIds.value.includes(currentCard.value.id))

const setFavoriteState = (cardId, isFavorite) => {
  const idSet = new Set(favoriteCardIds.value)

  if (isFavorite) {
    idSet.add(cardId)
  } else {
    idSet.delete(cardId)
  }

  favoriteCardIds.value = Array.from(idSet)
}

const loadFavoriteState = async (cardId) => {
  if (!cardId || favoriteLoadingIds.value.includes(cardId)) {
    return
  }

  favoriteLoadingIds.value = [...favoriteLoadingIds.value, cardId]
  try {
    const favorite = await getFavorites(TPR_CONTENT_TYPE, cardId)
    setFavoriteState(cardId, favorite != null)
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== cardId)
  }
}

const toggleFavorite = async () => {
  if(isGuestUser.value) {
    openLoginPrompt()
    return
  }

  const cardId = currentCard.value.id
  if (!cardId || favoriteLoadingIds.value.includes(cardId)) {
    return
  }

  const wasFavorite = favoriteCardIds.value.includes(cardId)
  favoriteLoadingIds.value = [...favoriteLoadingIds.value, cardId]
  setFavoriteState(cardId, !wasFavorite)

  try {
    if (wasFavorite) {
      await deleteFavorites(TPR_CONTENT_TYPE, cardId)
    } else {
      await addFavorites({
        contentType: TPR_CONTENT_TYPE,
        contentId: cardId
      })
    }
  } catch {
    setFavoriteState(cardId, wasFavorite)
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== cardId)
  }
}

const openLoginPrompt = () => {
  router.replace('/login')
}

const ensureCanUseTprFullscreen = () => {
  if (isGuestUser.value) {
    router.replace('/login')
    return false
  }

  if (isNonVipUser.value) {
    router.replace('/vip')
    return false
  }

  return true
}

const playVideo = async (restart = true) => {
  if (!ensureCanUseTprFullscreen()) {
    return
  }

  const video = fullscreenVideoRef.value
  if (!video) return

  if (restart) {
    video.currentTime = 0
  }

  try {
    await video.play()
  } catch (error) {
    isPlaying.value = false
  }
}

// 切换清屏模式
const toggleCleanMode = () => {
  isCleanMode.value = !isCleanMode.value
}

// 切换播放/暂停
const togglePlay = () => {
  if (isPlaying.value) {
    stopPlay()
  } else {
    void startPlay()
  }
}

// 开始播放
const startPlay = async (restart = true) => {
  if (!ensureCanUseTprFullscreen()) {
    return
  }

  if (!currentCard.value.videoUrl) {
    return
  }

  await nextTick()
  await playVideo(restart)
  
  if (isAutoMode.value && !autoPlayTimer.value) {
    autoPlayTimer.value = setInterval(() => {
      nextCard(false)
    }, 3000)
  }
}

// 停止播放
const stopPlay = () => {
  isPlaying.value = false
  const video = fullscreenVideoRef.value
  if (video) {
    video.pause()
    video.currentTime = 0
  }

  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
    autoPlayTimer.value = null
  }
}

// 开始自动播放
const startAutoPlay = () => {
  if (!ensureCanUseTprFullscreen()) {
    return
  }

  if (cards.value.length === 0) {
    return
  }

  if (isAutoMode.value) {
    isAutoMode.value = false
    stopPlay()
    return
  }

  isAutoMode.value = true
  void startPlay()
}



// 打开设置
const openSettings = () => {
  showSettings.value = true
}

const closeSettings = () => {
  showSettings.value = false
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/card')
}

// 上一张
const prevCard = (isManual = true) => {
  if (cards.value.length === 0) {
    return
  }

  stopPlay()
  currentIndex.value = (currentIndex.value - 1 + cards.value.length) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
  if (isAutoMode.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
}

// 下一张
const nextCard = (isManual = true) => {
  if (cards.value.length === 0) {
    return
  }

  stopPlay()
  currentIndex.value = (currentIndex.value + 1) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
  if (isAutoMode.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
}

// 触摸滑动
let touchStartX = 0
let touchEndX = 0

const handleTouchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX
}

const handleTouchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX
  handleSwipe()
}

const handleSwipe = () => {
  const swipeThreshold = 50
  const diff = touchStartX - touchEndX
  
  if (Math.abs(diff) > swipeThreshold) {
    if (diff > 0) {
      // 向左滑，下一张
      nextCard()
    } else {
      // 向右滑，上一张
      prevCard()
    }
  }
}

// 键盘事件
const handleKeydown = (e) => {
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    prevCard()
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    nextCard()
  } else if (e.key === ' ') {
    e.preventDefault()
    togglePlay()
  } else if (e.key === 'Escape') {
    router.push('/category')
  }
}

const handleVideoPlay = () => {
  if (!ensureCanUseTprFullscreen()) {
    const video = fullscreenVideoRef.value
    if (video) {
      video.pause()
      video.currentTime = 0
    }
    return
  }

  isPlaying.value = true
  if(!isGuestUser.value) {
    addPlayHistory(currentCard.value.id)
  }
}

const setCardAspectRatio = (cardId, width, height) => {
  if (!cardId || !width || !height) {
    return
  }

  videoAspectRatios.value = {
    ...videoAspectRatios.value,
    [String(cardId)]: `${width} / ${height}`
  }
}

const preloadCoverAspectRatio = (card) => {
  if (!card?.id || !card.coverUrl || videoAspectRatios.value[String(card.id)]) {
    return
  }

  const image = new Image()
  image.onload = () => {
    setCardAspectRatio(card.id, image.naturalWidth, image.naturalHeight)
  }
  image.src = card.coverUrl
}

const handleVideoMetadata = (event: Event) => {
  const video = event.target as HTMLVideoElement | null
  const cardId = currentCard.value.id
  if (!video?.videoWidth || !video?.videoHeight) {
    return
  }

  setCardAspectRatio(cardId, video.videoWidth, video.videoHeight)
}

const handleVideoLoadedData = () => {
  isVideoReady.value = true
}

const addPlayHistory = (cardId) => {
  if (!cardId || historyRecordedIds.has(cardId)) {
    return
  }

  historyRecordedIds.add(cardId)
  addHistory({
    contentType: TPR_CONTENT_TYPE,
    contentId: cardId
  }).catch(() => {
    historyRecordedIds.delete(cardId)
  })
}

const handleVideoPause = () => {
  if (!isAutoMode.value) {
    isPlaying.value = false
  }
}

const handleVideoEnded = () => {
  if (!isAutoMode.value) {
    isPlaying.value = false
  }
}

const getList = async () => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    tprList.value = await getTprList(queryParams.value) || []
    currentIndex.value = 0
  } catch {
    tprList.value = []
    currentIndex.value = 0
  } finally {
    isLoading.value = false
  }
}

watch(
  () => [currentCard.value.id, currentCard.value.coverUrl, currentCard.value.videoUrl],
  ([cardId]) => {
    isVideoReady.value = false
    preloadCoverAspectRatio(currentCard.value)
    if(!isGuestUser.value) {
      loadFavoriteState(cardId)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  window.addEventListener('keydown', handleKeydown)
  document.addEventListener('touchstart', handleTouchStart, { passive: true })
  document.addEventListener('touchend', handleTouchEnd, { passive: true })
  await getList()
  
  if (ensureCanUseTprFullscreen()) {
    setTimeout(() => {
      void startPlay()
    }, 500)
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchend', handleTouchEnd)
  stopPlay()
})
</script>

<style scoped>
/* 基础样式 */
.fullscreen-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #FFF8F0 0%, #F9F7F2 50%, #F5F0E8 100%);
  position: relative;
  overflow: hidden;
  user-select: none;
}

/* 背景装饰 */
.bg-gradient {
  position: fixed;
  inset: 0;
  z-index: -3;
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.6;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #FFD89B, #FF8C42);
  top: -100px;
  right: -100px;
  animation: float 8s ease-in-out infinite;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #A8E6CF, #4ECDC4);
  bottom: -50px;
  left: -50px;
  animation: float 10s ease-in-out infinite reverse;
}

.orb-3 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #FFB6C1, #FF6B6B);
  top: 50%;
  left: 20%;
  animation: float 12s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(30px, -30px); }
}

.back-btn {
  position: fixed;
  top: 32px;
  left: 24px;
  z-index: 110;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease,
    color 0.25s ease;
}

.back-btn :deep(svg) {
  color: inherit;
}

.back-btn:hover {
  transform: scale(1.06);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  color: #ff8a3d;
}

.back-btn:active {
  transform: scale(0.96);
}

/* 顶部标题 */
.page-header {
  position: fixed;
  top: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  text-align: center;
  transition: opacity 0.28s ease;
  width: min(560px, 64vw);
  pointer-events: none;
}

.page-header.hidden {
  opacity: 0;
  pointer-events: none;
}

.word-translation {
  font-size: clamp(24px, 2.5vw, 34px);
  font-weight: 700;
  color: var(--text, #8e939c);
  opacity: 0.9;
  line-height: 1.15;
  transition: all 0.3s ease;
}

/* 右侧控制按钮 */
.side-controls {
  position: fixed;
  right: 30px;
  top: 35%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 100;
  transition: opacity 0.3s ease;
}

.side-controls.hidden {
  opacity: 0;
  pointer-events: none;
}

.control-btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: var(--text, #2D3436);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.control-btn :deep(svg) {
  color: inherit;
}

.control-btn:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
  color: #ff8a3d;
}

.control-btn:active {
  transform: scale(0.95);
}

.control-btn:disabled {
  cursor: wait;
  opacity: 0.68;
}

.control-btn:disabled:hover {
  transform: none;
}

.favorite-btn.active {
  color: #ff8a3d;
  background: rgba(255, 246, 237, 0.95);
  border-color: rgba(255, 169, 97, 0.72);
  box-shadow: 0 8px 26px rgba(255, 138, 61, 0.22);
}

.favorite-btn.active:hover {
  color: #ff7534;
}

.auto-play-btn.active {
  color: #ff8a3d;
  background: rgba(255, 246, 237, 0.95);
  border-color: rgba(255, 169, 97, 0.72);
  box-shadow: 0 8px 26px rgba(255, 138, 61, 0.22);
}

.auto-play-btn.active:hover {
  color: #ff7534;
}

/* 主内容区 */
.main-content {
  position: fixed;
  top: 48%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(16px, 2.2vh, 26px);
  width: 100%;
  max-width: 760px;
  padding: 92px 20px 140px;
  max-height: 100svh;
  overflow: hidden;
}

/* 视频容器 */
.video-container {
  --tpr-video-radius: clamp(28px, 4.8vw, 60px);
  width: min(540px, 66vw, 52vh);
  aspect-ratio: var(--tpr-video-aspect-ratio, 4 / 5);
  border-radius: var(--tpr-video-radius);
  clip-path: inset(0 round var(--tpr-video-radius));
  overflow: hidden;
  cursor: pointer;
  flex: 0 1 auto;
  transform: translateZ(0);
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  background: transparent;
  border-radius: inherit;
  clip-path: inset(0 round var(--tpr-video-radius));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: all 0.3s ease;
  transform: translateZ(0);
}

.video-wrapper:hover {
  transform: scale(1.05);
}

.fullscreen-video {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  border-radius: inherit;
  clip-path: inset(0 round var(--tpr-video-radius));
  background: transparent;
  opacity: 0;
  transform: translateZ(0);
}

.fullscreen-video.is-ready {
  opacity: 1;
}

.fullscreen-video-cover {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  border-radius: inherit;
  clip-path: inset(0 round var(--tpr-video-radius));
  background: transparent;
  pointer-events: none;
  transform: translateZ(0);
}

.fullscreen-video::-webkit-media-controls {
  display: none !important;
}

.fullscreen-video::-webkit-media-controls-enclosure {
  display: none !important;
}

.video-status {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 24px;
  pointer-events: none;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
}

/* 单词展示 */
.word-display {
  max-width: min(760px, calc(100vw - 220px));
  margin: 0;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: clamp(48px, 6vw, 72px);
  font-weight: 800;
  color: var(--text, #2D3436);
  letter-spacing: 0;
  line-height: 1;
  transition: all 0.3s ease;
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 底部控制栏 */
.bottom-controls {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  transition: opacity 0.3s ease;
}

.bottom-controls.hidden {
  opacity: 0;
  pointer-events: none;
}

.play-section {
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-btn {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF8C42, #FF6B35);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 40px rgba(255, 140, 66, 0.4);
}

.play-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 15px 50px rgba(255, 140, 66, 0.5);
}

.play-btn:active {
  transform: scale(0.95);
}

/* 进度指示 */
.progress-indicator {
  position: fixed;
  bottom: 50px;
  left: 30px;
  z-index: 100;
}

.progress-text {
  font-size: 24px;
  font-weight: 700;
  color: var(--text, #2D3436);
  opacity: 0.7;
}

/* 左右切换按钮 */
.nav-btn {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #8e939c;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.nav-btn :deep(svg) {
  color: inherit;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-50%) scale(1.1);
  color: #ff8a3d;
}

.nav-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.nav-btn.prev {
  left: 30px;
}

.nav-btn.next {
  right: 30px;
}

/* 清屏模式 */
.clean-mode .nav-btn,
.clean-mode .side-controls {
  opacity: 0;
  pointer-events: none;
}

.clean-mode .word-display {
  font-size: clamp(72px, 9vw, 110px);
}

.clean-mode .word-translation {
  font-size: 48px;
}

/* 触摸区域 */
.swipe-area {
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.settings-open .main-content {
  opacity: 0;
  pointer-events: none;
}

.settings-open .video-container,
.settings-open .fullscreen-video {
  display: none;
}

/* 设置弹窗 */
.settings-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483647;
  backdrop-filter: blur(6px);
  padding: 20px;
  transform: translateZ(0);
}

.settings-content {
  position: relative;
  z-index: 1;
  width: min(780px, 94vw);
  background: #ECECEC;
  border-radius: 18px;
  box-shadow: 0 24px 52px rgba(0, 0, 0, 0.22);
  padding: 16px 0;
}

.settings-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
}

.settings-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.settings-title {
  margin: 0;
  color: #1b1b1f;
  font-size: clamp(18px, 2.8vw, 28px);
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
}

.settings-desc {
  margin: 0;
  color: #6b6b73;
  font-size: clamp(12px, 1.4vw, 18px);
  line-height: 1.35;
  white-space: nowrap;
}

/* 开关样式 */
.switch {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: 70px;
  height: 38px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #cfcfd2;
  border-radius: 999px;
  transition: all 0.25s ease;
}

.slider::before {
  content: "";
  position: absolute;
  width: 30px;
  height: 30px;
  left: 4px;
  top: 4px;
  border-radius: 50%;
  background: #f2f2f2;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.25s ease;
}

.switch input:checked + .slider {
  background: #ff6f35;
}

.switch input:checked + .slider::before {
  transform: translateX(32px);
}

@media (hover: hover) and (pointer: fine) {
  .back-btn {
    display: none;
  }
}

@media (min-width: 769px) and (max-width: 1180px) {
  .main-content {
    top: 50%;
    gap: clamp(14px, 2svh, 22px);
    padding: 82px 20px 124px;
  }

  .video-container {
    width: auto;
    height: clamp(330px, 52svh, 540px);
    max-width: min(72vw, 560px);
    aspect-ratio: var(--tpr-video-aspect-ratio, 4 / 5);
  }

  .word-display {
    max-width: calc(100vw - 180px);
    font-size: clamp(44px, 5.4vw, 64px);
  }
}

/* 移动端适配 */
@media (max-width: 768px),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .fullscreen-page {
    min-height: 100svh;
  }

  .back-btn {
    display: inline-flex;
    top: calc(env(safe-area-inset-top, 0px) + 18px);
    left: 16px;
    width: 52px;
    height: 52px;
  }

  .page-header {
    top: calc(env(safe-area-inset-top, 0px) + 24px);
    width: min(58vw, 360px);
  }

  .word-translation {
    font-size: 28px;
    line-height: 1.2;
  }

  .video-container {
    width: min(390px, 88vw, 54vh);
  }

  .main-content {
    top: 47%;
    gap: 22px;
    padding: 0 20px;
    max-height: none;
    overflow: visible;
  }

  .video-wrapper:hover {
    transform: none;
  }

  .word-display {
    max-width: calc(100vw - 32px);
    font-size: 48px;
    letter-spacing: 2px;
    line-height: 1.05;
  }

  .nav-btn {
    top: auto;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 25px);
    width: 58px;
    height: 58px;
    font-size: 20px;
    transform: none;
  }

  .nav-btn.prev {
    left: calc(50% - 116px);
  }

  .nav-btn.next {
    right: calc(50% - 116px);
  }

  .nav-btn:hover {
    transform: none;
  }

  .nav-btn:active {
    transform: scale(0.95);
  }

  .side-controls {
    top: auto;
    right: auto;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 104px);
    left: 50%;
    flex-direction: row;
    justify-content: space-between;
    gap: 12px;
    width: min(240px, calc(100vw - 72px));
    padding: 8px 12px;
    border: 1px solid rgba(255, 255, 255, 0.72);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.52);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    box-shadow: 0 12px 32px rgba(45, 52, 54, 0.12);
    transform: translateX(-50%);
  }

  .control-btn {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .bottom-controls {
    bottom: calc(env(safe-area-inset-bottom, 0px) + 18px);
  }

  .play-btn {
    width: 72px;
    height: 72px;
    font-size: 28px;
  }

  .progress-indicator {
    top: auto;
    right: auto;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 38px);
    left: 18px;
    transform: none;
    padding: 5px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.58);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 8px 22px rgba(45, 52, 54, 0.08);
  }

  .progress-text {
    font-size: 15px;
    line-height: 1;
  }


  .settings-content {
    width: min(560px, 96vw);
    padding: 10px 0;
  }

  .settings-item {
    padding: 14px 16px;
  }

  .settings-title {
    font-size: clamp(16px, 3.8vw, 22px);
  }

  .settings-desc {
    font-size: clamp(11px, 2.4vw, 15px);
  }

  .switch {
    width: 64px;
    height: 34px;
  }

  .slider::before {
    width: 26px;
    height: 26px;
    left: 4px;
    top: 4px;
  }

  .switch input:checked + .slider::before {
    transform: translateX(30px);
  }
  .clean-mode .word-display {
    font-size: 60px;
  }

  .clean-mode .word-translation {
    font-size: 32px;
  }
}

@media (max-width: 480px) {
  .video-container {
    width: min(340px, 90vw, 50vh);
  }

  .word-display {
    font-size: 36px;
  }

  .main-content {
    top: 46%;
    gap: 18px;
  }

  .nav-btn {
    bottom: calc(env(safe-area-inset-bottom, 0px) + 23px);
    width: 54px;
    height: 54px;
  }

  .nav-btn.prev {
    left: calc(50% - 108px);
  }

  .nav-btn.next {
    right: calc(50% - 108px);
  }

  .side-controls {
    bottom: calc(env(safe-area-inset-bottom, 0px) + 98px);
    width: min(226px, calc(100vw - 64px));
  }

  .bottom-controls {
    bottom: calc(env(safe-area-inset-bottom, 0px) + 16px);
  }

  .progress-indicator {
    bottom: calc(env(safe-area-inset-bottom, 0px) + 36px);
    left: 16px;
  }

  .settings-content {
    width: 96vw;
  }

  .settings-item {
    padding: 12px 14px;
  }

  .settings-title {
    font-size: 14px;
    line-height: 1.25;
  }

  .settings-desc {
    font-size: 11px;
  }

  .switch {
    width: 58px;
    height: 30px;
  }

  .slider::before {
    width: 22px;
    height: 22px;
    left: 4px;
    top: 4px;
  }

  .switch input:checked + .slider::before {
    transform: translateX(28px);
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .orb {
    display: none;
  }

  .video-container {
    aspect-ratio: var(--tpr-video-aspect-ratio, 4 / 5);
  }

  .fullscreen-video {
    object-fit: contain;
  }

  .top-bar,
  .control-panel,
  .settings-content,
  .settings-mask,
  .bottom-controls,
  .side-controls {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .control-btn,
  .nav-btn,
  .settings-btn,
  .settings-close,
  .mode-option {
    transition-property: background-color, color, border-color, box-shadow, transform, opacity;
  }
}
</style>
